import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { Icon } from 'antd'
import { useTranslation } from 'react-i18next'
import { Popup } from '@/components/atoms/popup'
import { Button, ButtonType } from '@/components/atoms/button'
import { useAppDispatch } from '@/features/hooks'
import { saveColumnSetting } from '@/features/restaurant-manager/restaurant-manager-thunks'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { draggableIconStyle, useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import type { DisplayColumnSetting } from '@/models/restaurant-manager'
import { getRestaurantOverviewColumnLabelKey } from '@/models/restaurant-manager'

type Props = {
  defaultColumns: DisplayColumnSetting[]
  hasMealSpacePlugin: boolean
}

export const RestaurantColumnSettingPopover: React.FC<Props> = ({ defaultColumns, hasMealSpacePlugin }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { errorHandler } = useErrorHandler()
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [columns, setColumns] = useState<DisplayColumnSetting[]>(() => [])

  const sortableColumns = columns.filter(col => !col.isFixed)

  useEffect(() => {
    if (!isOpen) {
      setColumns(
        [...defaultColumns]
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map(column => ({
            ...column,
            isVisible: column.columnKey === 'spaceReservationDetails' && !hasMealSpacePlugin ? false : column.isVisible,
          })),
      )
    }
  }, [defaultColumns, isOpen])

  const { dragStart, dragOver, dragDrop, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom<DisplayColumnSetting>({
    list: sortableColumns,
    onChange: newSortableColumns => {
      const fixedColumns = columns.filter(col => col.isFixed).sort((a, b) => a.displayOrder - b.displayOrder)
      const fixedCount = fixedColumns.length
      const updatedSortableColumns = newSortableColumns.map((col, index) => ({
        ...col,
        displayOrder: fixedCount + index,
      }))
      setColumns([...fixedColumns, ...updatedSortableColumns])
    },
    key: 'columnKey',
  })

  const closePopover = () => {
    if (isSaving) {
      return
    }
    setIsOpen(false)
  }

  const toggleVisibility = (columnKey: DisplayColumnSetting['columnKey']) => {
    setColumns(currentColumns =>
      currentColumns.map(column => {
        if (column.columnKey !== columnKey || column.isRequired || isMealSpaceDisabledByPlugin(column, hasMealSpacePlugin)) {
          return column
        }

        return {
          ...column,
          isVisible: !column.isVisible,
        }
      }),
    )
  }

  const getCheckboxImagePath = (column: DisplayColumnSetting) => {
    if (!column.isVisible) {
      return require('@/static/images/room/check-box_off.svg')
    }

    return column.isRequired || isMealSpaceDisabledByPlugin(column, hasMealSpacePlugin)
      ? require('@/static/images/room/check-box_gray_on.svg')
      : require('@/static/images/room/check-box_yellow_on.svg')
  }

  const saveColumns = async () => {
    try {
      setIsSaving(true)
      await dispatch(
        saveColumnSetting(
          columns.map((column, index) => ({
            ...column,
            displayOrder: index,
          })),
        ),
      ).unwrap()
      setIsOpen(false)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div css={containerStyle} onClick={event => event.stopPropagation()}>
      <button type="button" css={triggerStyle(isOpen)} onClick={() => setIsOpen(current => !current)}>
        <Icon type="setting" theme="filled" css={triggerIconStyle} />
        <span>{t('RestaurantManager.DisplaySettings')}</span>
      </button>

      <Popup isOpen={isOpen} onClose={closePopover} customStyle={popupStyle}>
        <div css={panelStyle}>
          <div css={headerStyle}>
            <div css={titleStyle}>{t('RestaurantManager.DisplayColumns')}</div>
            <div css={descriptionStyle}>{t('RestaurantManager.DisplayColumnsDescription')}</div>
          </div>

          <div css={columnListStyle}>
            {columns.map(column => {
              const isMealSpaceDisabled = isMealSpaceDisabledByPlugin(column, hasMealSpacePlugin)
              const isVisibilityToggleDisabled = column.isRequired || column.isFixed || isMealSpaceDisabled

              return (
                <div
                  key={column.columnKey}
                  id={column.columnKey}
                  css={[columnItemStyle, isMealSpaceDisabled && mealSpaceDisabledRowStyle, !column.isFixed && draggableIconStyle]}
                  draggable={!column.isFixed}
                  onDragStart={!column.isFixed ? dragStart : undefined}
                  onDragOver={!column.isFixed ? dragOver : undefined}
                  onDrop={!column.isFixed ? dragDrop : undefined}
                  onTouchStart={!column.isFixed ? handleTouchStart : undefined}
                  onTouchMove={!column.isFixed ? handleTouchMove : undefined}
                  onTouchEnd={!column.isFixed ? handleTouchEnd : undefined}
                >
                  <button
                    type="button"
                    css={checkboxButtonStyle(isVisibilityToggleDisabled)}
                    disabled={isVisibilityToggleDisabled}
                    onClick={() => toggleVisibility(column.columnKey)}
                  >
                    <img draggable={false} css={checkboxIconStyle} src={getCheckboxImagePath(column)} alt="" />
                  </button>

                  <div css={columnNameStyle(isVisibilityToggleDisabled)}>
                    {t(getRestaurantOverviewColumnLabelKey(column.columnKey))}
                    {column.isFixed && <span css={fixedLabelStyle}>{`(${t('dashboard.fixed')})`}</span>}
                  </div>
                  {column.isRequired && <div css={requiredLabelStyle}>{t('RestaurantManager.RequiredColumn')}</div>}
                </div>
              )
            })}
          </div>

          <div css={footerStyle}>
            <Button buttonType={ButtonType.Secondary} height={36} width={96} onClick={closePopover}>
              {t('Cancel')}
            </Button>
            <Button buttonType={ButtonType.Primary} height={36} width={96} onClick={saveColumns}>
              {isSaving ? t('Loading') : t('Save')}
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  )
}

const containerStyle = css({
  position: 'relative',
})

const triggerStyle = (isOpen: boolean) =>
  css({
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    color: isOpen ? '#272727' : '#676767',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 12,
    fontWeight: 'bold',
    gap: 8,
    padding: 0,
  })

const triggerIconStyle = css({
  fontSize: 14,
})

const popupStyle = css({
  right: 0,
  top: 28,
})

const panelStyle = css({
  background: '#FFFFFF',
  borderRadius: 5,
  overflow: 'hidden',
  width: 360,
})

const headerStyle = css({
  borderBottom: '1px solid #F2F2F2',
  padding: '20px 20px 14px',
})

const titleStyle = css({
  color: '#676767',
  fontWeight: 'bold',
  fontSize: 14,
  marginBottom: 6,
})

const descriptionStyle = css({
  color: '#999999',
  fontSize: 12,
  lineHeight: 1.6,
})

const columnListStyle = css({
  maxHeight: 350,
  overflowY: 'auto',
})

const columnItemStyle = css({
  alignItems: 'center',
  borderBottom: '1px solid #F2F2F2',
  display: 'flex',
  gap: 8,
  minHeight: 42,
  padding: '0 12px 0 48px',
  '&:hover': {
    backgroundColor: '#FAFAFA',
  },
})

const mealSpaceDisabledRowStyle = css({
  background: '#F5F5F5',
})

const isMealSpaceDisabledByPlugin = (column: DisplayColumnSetting, hasMealSpacePlugin: boolean) =>
  column.columnKey === 'spaceReservationDetails' && !hasMealSpacePlugin

const checkboxButtonStyle = (isDisabled: boolean) =>
  css({
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    cursor: isDisabled ? 'default' : 'pointer',
    display: 'inline-flex',
    flex: '0 0 auto',
    justifyContent: 'center',
    padding: 0,
    opacity: isDisabled ? 0.65 : 1,
  })

const checkboxIconStyle = css({
  display: 'block',
  height: 22,
  width: 22,
})

const columnNameStyle = (isDisabled: boolean) =>
  css({
    color: isDisabled ? '#999999' : '#676767',
    flex: 1,
    fontSize: 14,
    fontWeight: 'bold',
    minWidth: 0,
  })

const requiredLabelStyle = css({
  color: '#CCCCCC',
  flex: '0 0 auto',
  fontSize: 12,
  fontWeight: 'bold',
})

const fixedLabelStyle = css({
  color: '#CCCCCC',
  fontWeight: 'bold',
  fontSize: 12,
  lineHeight: 1.5,
  marginLeft: 4,
})

const footerStyle = css({
  alignItems: 'center',
  background: '#FFFFFF',
  borderTop: '1px solid #F2F2F2',
  display: 'flex',
  gap: 12,
  justifyContent: 'flex-end',
  padding: 16,
})
